import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { useHighlightedGroups, useHighlightedSecurities, useHoldingsMetric, useTopBottomSecurities } from '@/contexts/HoldingsTabContext'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps, RiskVisualizationPrefs, RiskVisualizationViewMode, cardPrefsContextKey } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import GridViewIcon from '@mui/icons-material/GridView'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useMemo, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'
import FilterIndicatorBox from '../Shared/FilterIndicatorBox'

const RiskVisualCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const allSecurityDetails = useHighlightedSecurities();
  const highlightedSecurityDetails = useHighlightedSecurities();
  const { showContributors, contributorCount, securitiesFiltered } = useHoldingsMetric()
  const topBotItems = useTopBottomSecurities();
  const [viewMode, setViewMode] = useState<RiskVisualizationViewMode>(
    props.cardPrefs?.holdingsRiskVisualization?.viewMode ?? RiskVisualizationViewMode.TopBottom,
  )

  useEffect(() => {
    const saved = props.cardPrefs?.holdingsRiskVisualization
    if (saved) {
      setViewMode(saved.viewMode)
    }
  }, [props.cardPrefs])

  const savePrefs = (newPrefs: RiskVisualizationPrefs) => {
    if (props.cardPrefs?.dashboardName) {
      const updated = { ...props.cardPrefs, holdingsRiskVisualization: newPrefs }
      props.onCardPrefsChange?.(updated)
      putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), updated)
    }
  }

  const { topItems, bottomItems } = topBotItems;

  const cardLayout = {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    padding: '12px',
    boxSizing: 'border-box' as const,
  }

  const chartOptions = useMemo((): Highcharts.Options => {
    const dataset: typeof topItems =
      viewMode === RiskVisualizationViewMode.AllHoldings && allSecurityDetails != null
        ? allSecurityDetails
        : [...topItems, ...bottomItems]

    const mvValues = dataset
      .map((d) => d.marketValueMillions)
      .filter((v): v is number => v != null && Number.isFinite(v))
    const minMv = mvValues.length ? Math.min(...mvValues) : 0
    const maxMv = mvValues.length ? Math.max(...mvValues) : 0
    const minR = 6, maxR = 24
    const radiusFor = (mv: number | null | undefined): number => {
      if (mv == null || !Number.isFinite(mv)) return minR
      if (maxMv === minMv) return (minR + maxR) / 2
      return minR + ((mv - minMv) / (maxMv - minMv)) * (maxR - minR)
    }

    const makePoints = (data: typeof topItems) =>
      data.map((d) => ({
        x: d.securityRisk,
        y: d.securityRiskContrib,
        name: d.issuer,
        marketValue: d.marketValueMillions,
        marker: { radius: radiusFor(d.marketValueMillions) },
      }))

    const series: Highcharts.SeriesOptionsType[] = []

    if (viewMode === RiskVisualizationViewMode.AllHoldings && allSecurityDetails != null) {
      series.push({
        type: 'scatter',
        name: 'All Holdings',
        data: makePoints(allSecurityDetails),
        color: getColorWithOpacity(COMPANY_COLORS.MEDIUM_BLUE, 0.55),
        marker: { symbol: 'circle', lineColor: COMPANY_COLORS.MEDIUM_BLUE, lineWidth: 1.5 },
        dataLabels: {
          enabled: true,
          format: '{point.name}',
          style: { fontSize: '14px', fontWeight: '500', color: theme.textPrimary, textOutline: 'none' },
        },
      } as Highcharts.SeriesScatterOptions)
    } else {
      const showTop = showContributors !== 'bottomOnly'
      const showBottom = showContributors !== 'topOnly'

      if (showTop) {
        series.push({
          type: 'scatter',
          name: `Top ${contributorCount}`,
          data: makePoints(topItems),
          color: getColorWithOpacity(COMPANY_COLORS.ERROR, 0.45),
          marker: { symbol: 'circle', lineColor: COMPANY_COLORS.ERROR, lineWidth: 1.5 },
          dataLabels: {
            enabled: true,
            format: '{point.name}',
            style: { fontSize: '14px', fontWeight: '500', color: theme.textPrimary, textOutline: 'none' },
          },
        } as Highcharts.SeriesScatterOptions)
      }

      if (showBottom) {
        series.push({
          type: 'scatter',
          name: `Bottom ${contributorCount}`,
          data: makePoints(bottomItems),
          color: getColorWithOpacity(COMPANY_COLORS.SUCCESS, 0.55),
          marker: { symbol: 'circle', lineColor: COMPANY_COLORS.SUCCESS, lineWidth: 1.5 },
          dataLabels: {
            enabled: true,
            format: '{point.name}',
            style: { fontSize: '14px', fontWeight: '500', color: theme.textPrimary, textOutline: 'none' },
          },
        } as Highcharts.SeriesScatterOptions)
      }
    }

    return {
      chart: {
        events: {
          selection: (e) => {
            // This event fires when the chart is zoomed into or the zoom is reset.
            const zoomReset = !!e.resetSelection; // When zoom is reset `resetSelection` is true. Otherwise, undefined.
            if (!zoomReset) {
              // Holdings chart only contains a single axis but a Highchart may contain multiple.
              const xAxisData = e.xAxis[0];
              const yAxisData = e.yAxis[0];
            }
          }
        },
        zooming: {
          type: 'xy'
        },
        type: 'scatter',
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        animation: false,
        spacing: [8, 16, 8, 8],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { color: theme.textPrimary, fontSize: '14px', fontWeight: '400' },
        itemHoverStyle: { color: theme.textPrimary },
      },
      xAxis: {
        events: {
          afterSetExtremes: (e) => {
            // Event fires when the X Axis changes.
          }
        },
        title: { text: 'Security Risk', style: { color: theme.textPrimary, fontSize: '14px' } },
        labels: { style: { color: theme.textPrimary, fontSize: '14px' } },
        gridLineColor: theme.border,
        lineColor: theme.border,
        tickColor: theme.border,
        softMax: 1
      },
      yAxis: {
        events: {
          afterSetExtremes: (e) => {
            // Event fires after the Y axis changes
          }
        },
        title: { text: 'Contribution to Risk', style: { color: theme.textPrimary, fontSize: '14px' } },
        labels: { format: '{value:.2f}', style: { color: theme.textPrimary, fontSize: '14px' } },
        gridLineColor: theme.border,
        softMax: 1,
        softMin: 0
      },
      tooltip: {
        backgroundColor: theme.cardBackground,
        borderColor: theme.border,
        style: { color: theme.textPrimary, fontSize: '14px' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const mv = (this.point as any).marketValue as number | null | undefined
          const mvLine = mv != null && Number.isFinite(mv)
            ? `<br/>Market Value: <b>$${mv.toFixed(1)}M</b>`
            : ''
          return `<b>${(this.point as any).name}</b><br/>
            Security Risk: <b>${(this.x as number).toFixed(2)}</b><br/>
            Contribution: <b>${(this.y as number).toFixed(2)}</b>${mvLine}`
        },
      },
      plotOptions: {
        scatter: {
          marker: { states: { hover: { enabled: true } } },
        },
      },
      series,
    }
  }, [viewMode, showContributors, contributorCount, theme, topItems, bottomItems, allSecurityDetails, highlightedSecurityDetails])
  const highlightGroups = useHighlightedGroups();
  const filteredBox = !securitiesFiltered ? null : <FilterIndicatorBox groups={highlightGroups} />;

  return (
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: '12px' }}>
        <Stack>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
            {CardTitleLookup[props.cardId]} {filteredBox}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>
            Graphical representation of risk contributors
          </Typography>
        </Stack>
        <Button
          size='small'
          startIcon={<GridViewIcon sx={{ fontSize: 14 }} />}
          onClick={() => {
            const next = viewMode === RiskVisualizationViewMode.AllHoldings ? RiskVisualizationViewMode.TopBottom : RiskVisualizationViewMode.AllHoldings
            setViewMode(next)
            savePrefs({ viewMode: next })
          }}
          sx={{
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: '6px',
            padding: '4px 10px',
            backgroundColor: 'transparent',
            color: theme.textPrimary,
            border: `1px solid ${theme.border}`,
            '&:hover': {
              backgroundColor: theme.border + '44',
            },
          }}
        >
          {viewMode === RiskVisualizationViewMode.AllHoldings ? 'All Holdings' : `Top / Bottom ${contributorCount}`}
        </Button>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          updateArgs={[true, true, false]}
          containerProps={{ style: { height: '100%', width: '100%' } }}
        />
      </Box>
    </Stack>
  )
}

export default RiskVisualCard
